import {
  __privateAdd,
  __privateMethod
} from "./chunk-GV6AIHPN.mjs";

// src/ethereum/siwe.ts
var ADDRESS_REGEX = /^0x[a-fA-F0-9]{40}$/;
var DOMAIN_REGEX = /^([a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}(:[0-9]{1,5})?$/;
var IP_REGEX = /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)(:[0-9]{1,5})?$/;
var LOCALHOST_REGEX = /^localhost(:[0-9]{1,5})?$/;
var NONCE_REGEX = /^[a-zA-Z0-9]{8,}$/;
var SCHEME_REGEX = /^([a-zA-Z][a-zA-Z0-9+-.]*)$/;
function createSiweMessage({
  address,
  chainId,
  domain,
  nonce,
  uri,
  version,
  scheme,
  statement: _statement,
  requestId,
  resources,
  issuedAt = /* @__PURE__ */ new Date(),
  expirationTime,
  notBefore
}) {
  if (!ADDRESS_REGEX.test(address)) {
    throw new Error("address must be a hex value of 20 bytes (40 hex characters).");
  }
  if (chainId !== Math.floor(chainId)) {
    throw new Error("chainId must be a EIP-155 chain ID.");
  }
  if (!(DOMAIN_REGEX.test(domain) || IP_REGEX.test(domain) || LOCALHOST_REGEX.test(domain))) {
    throw new Error("domain must be an RFC 3986 authority.");
  }
  if (!NONCE_REGEX.test(nonce)) {
    throw new Error("nonce must be at least 8 characters.");
  }
  if (!_isUri(uri)) {
    throw new Error("uri must be a RFC 3986 URI referring to the resource that is the subject of the signing.");
  }
  if (version !== "1") {
    throw new Error("version must be '1'.");
  }
  if (scheme && !SCHEME_REGEX.test(scheme)) {
    throw new Error("scheme must be an RFC 3986 URI scheme.");
  }
  if (_statement?.includes("\n")) {
    throw new Error("statement must not include '\\n'.");
  }
  const origin = scheme ? `${scheme}://${domain}` : domain;
  const statement = _statement ? `${_statement}
` : "";
  const prefix = `${origin} wants you to sign in with your Ethereum account:
${address}

${statement}`;
  let suffix = `URI: ${uri}
Version: ${version}
Chain ID: ${chainId}
Nonce: ${nonce}
Issued At: ${issuedAt.toISOString()}`;
  if (expirationTime) {
    suffix += `
Expiration Time: ${expirationTime.toISOString()}`;
  }
  if (notBefore) {
    suffix += `
Not Before: ${notBefore.toISOString()}`;
  }
  if (requestId) {
    suffix += `
Request ID: ${requestId}`;
  }
  if (resources) {
    let content = "\nResources:";
    for (const resource of resources) {
      if (!_isUri(resource)) {
        throw new Error("resources must be RFC 3986 URIs.");
      }
      content += `
- ${resource}`;
    }
    suffix += content;
  }
  return `${prefix}
${suffix}`;
}
function _isUri(value) {
  if (/[^a-z0-9:/?#[\]@!$&'()*+,;=.\-_~%]/i.test(value))
    return false;
  if (/%[^0-9a-f]/i.test(value))
    return false;
  if (/%[0-9a-f](:?[^0-9a-f]|$)/i.test(value))
    return false;
  const splitted = splitUri(value);
  const scheme = splitted[1];
  const authority = splitted[2];
  const path = splitted[3];
  const query = splitted[4];
  const fragment = splitted[5];
  if (!(scheme?.length && path.length >= 0))
    return false;
  if (authority?.length) {
    if (!(path.length === 0 || /^\//.test(path)))
      return false;
  } else {
    if (/^\/\//.test(path))
      return false;
  }
  if (!/^[a-z][a-z0-9+\-.]*$/.test(scheme.toLowerCase()))
    return false;
  let out = "";
  out += `${scheme}:`;
  if (authority?.length)
    out += `//${authority}`;
  out += path;
  if (query?.length)
    out += `?${query}`;
  if (fragment?.length)
    out += `#${fragment}`;
  return out;
}
function splitUri(value) {
  return value.match(/(?:([^:/?#]+):)?(?:\/\/([^/?#]*))?([^?#]*)(?:\?([^#]*))?(?:#(.*))?/);
}

// src/ethereum/strategies/injected.ts
var MAX_RETRIES = 4;
var BASE_DELAY = 100;
var _getProvider, getProvider_fn;
var InjectedEthereumStrategy = class {
  constructor() {
    __privateAdd(this, _getProvider);
    this.type = "injected" /* INJECTED */;
  }
  load() {
    let retryCount = 0;
    const scheduleRetry = (resolve, reject) => {
      const delay = BASE_DELAY * Math.pow(2, Math.min(retryCount, 5));
      setTimeout(() => {
        if (__privateMethod(this, _getProvider, getProvider_fn).call(this)) {
          resolve();
          return;
        }
        retryCount++;
        if (retryCount >= MAX_RETRIES) {
          reject();
        } else {
          scheduleRetry(resolve, reject);
        }
      }, delay);
    };
    return new Promise((resolve, reject) => {
      scheduleRetry(() => resolve(this), reject);
    });
  }
  getProvider() {
    return __privateMethod(this, _getProvider, getProvider_fn).call(this) || null;
  }
  get isConnected() {
    const provider = __privateMethod(this, _getProvider, getProvider_fn).call(this);
    return provider?.isConnected && provider.selectedAddress ? true : false;
  }
  async connect({ onlyIfTrusted }) {
    const provider = __privateMethod(this, _getProvider, getProvider_fn).call(this);
    if (!provider) {
      throw new Error("Provider not found.");
    }
    if (provider.isConnected && provider.selectedAddress) {
      return this.getAccounts();
    }
    try {
      const accounts = await provider.request({
        method: onlyIfTrusted ? "eth_accounts" : "eth_requestAccounts"
      });
      return accounts;
    } catch (_) {
      return void 0;
    }
  }
  async disconnect() {
    return Promise.resolve();
  }
  async getAccounts() {
    const provider = __privateMethod(this, _getProvider, getProvider_fn).call(this);
    if (!provider) {
      return [];
    }
    try {
      const accounts = await provider.request({ method: "eth_accounts" });
      return accounts || [];
    } catch (_) {
      return [];
    }
  }
  async signMessage(message, address) {
    const provider = __privateMethod(this, _getProvider, getProvider_fn).call(this);
    if (!provider) {
      throw new Error("Provider not found.");
    }
    if (!provider.isConnected) {
      throw new Error("Provider is not connected.");
    }
    const signature = await provider.request({
      method: "eth_sign",
      params: [address, message]
    });
    return signature;
  }
  async signPersonalMessage(message, address) {
    const provider = __privateMethod(this, _getProvider, getProvider_fn).call(this);
    if (!provider) {
      throw new Error("Provider not found.");
    }
    if (!provider.isConnected) {
      throw new Error("Provider is not connected.");
    }
    const signature = await provider.request({
      method: "personal_sign",
      params: [message, address]
    });
    return signature;
  }
  async signTypedData(typedData, address) {
    const provider = __privateMethod(this, _getProvider, getProvider_fn).call(this);
    if (!provider) {
      throw new Error("Provider not found.");
    }
    if (!provider.isConnected) {
      throw new Error("Provider is not connected.");
    }
    const signature = await provider.request({
      method: "eth_signTypedData_v4",
      params: [address, JSON.stringify(typedData)]
    });
    return signature;
  }
  async signIn(signInData) {
    const provider = __privateMethod(this, _getProvider, getProvider_fn).call(this);
    if (!provider) {
      throw new Error("Provider not found.");
    }
    const message = createSiweMessage(signInData);
    const address = provider.selectedAddress;
    if (!address) {
      throw new Error("No address available.");
    }
    const signature = await this.signPersonalMessage(message, address);
    return {
      address,
      signature,
      signedMessage: message
    };
  }
  async sendTransaction(transaction) {
    const provider = __privateMethod(this, _getProvider, getProvider_fn).call(this);
    if (!provider) {
      throw new Error("Provider not found.");
    }
    if (!provider.isConnected) {
      throw new Error("Provider is not connected.");
    }
    const txHash = await provider.request({
      method: "eth_sendTransaction",
      params: [transaction]
    });
    return txHash;
  }
  async signTransaction(transaction) {
    const provider = __privateMethod(this, _getProvider, getProvider_fn).call(this);
    if (!provider) {
      throw new Error("Provider not found.");
    }
    if (!provider.isConnected) {
      throw new Error("Provider is not connected.");
    }
    const signedTx = await provider.request({
      method: "eth_signTransaction",
      params: [transaction]
    });
    return signedTx;
  }
  async getChainId() {
    const provider = __privateMethod(this, _getProvider, getProvider_fn).call(this);
    if (!provider) {
      throw new Error("Provider not found.");
    }
    const chainId = await provider.request({ method: "eth_chainId" });
    return chainId;
  }
  async switchChain(chainId) {
    const provider = __privateMethod(this, _getProvider, getProvider_fn).call(this);
    if (!provider) {
      throw new Error("Provider not found.");
    }
    await provider.request({
      method: "wallet_switchEthereumChain",
      params: [{ chainId }]
    });
  }
  async request(args) {
    const provider = __privateMethod(this, _getProvider, getProvider_fn).call(this);
    if (!provider) {
      throw new Error("Provider not found.");
    }
    return await provider.request(args);
  }
};
_getProvider = new WeakSet();
getProvider_fn = function() {
  if (typeof window === "undefined") {
    return void 0;
  }
  return window?.phantom?.ethereum;
};

// src/ethereum/getProvider.ts
async function getProvider(strategy = "injected" /* INJECTED */) {
  if (strategy === "injected") {
    const provider = new InjectedEthereumStrategy();
    try {
      await provider.load();
      return provider;
    } catch (error) {
      throw new Error("Provider not found.");
    }
  } else {
    throw new Error("Invalid provider type.");
  }
}

// src/ethereum/eventListeners.ts
var eventListeners = /* @__PURE__ */ new Map();
function addEventListener(event, callback) {
  if (!eventListeners.has(event)) {
    eventListeners.set(event, /* @__PURE__ */ new Set());
  }
  const listeners = eventListeners.get(event);
  listeners?.add(callback);
  return () => removeEventListener(event, callback);
}
function removeEventListener(event, callback) {
  const listeners = eventListeners.get(event);
  if (listeners) {
    listeners.delete(callback);
  }
}
function triggerEvent(event, data) {
  const listeners = eventListeners.get(event);
  if (listeners) {
    listeners.forEach((callback) => {
      try {
        callback(data);
      } catch (error) {
        console.error(`Error in ${event} event listener:`, error);
      }
    });
  }
}

// src/ethereum/connect.ts
async function connect({ onlyIfTrusted = false } = {}) {
  const provider = await getProvider();
  if (!provider) {
    throw new Error("Provider not found.");
  }
  if (provider.isConnected) {
    const accounts = await provider.getAccounts();
    return accounts;
  }
  try {
    const accounts = await provider.connect({ onlyIfTrusted: true });
    if (accounts && accounts.length > 0) {
      triggerEvent("connect", accounts);
      return accounts;
    }
  } catch (error) {
  }
  if (onlyIfTrusted) {
    throw new Error("No trusted connection available.");
  }
  try {
    const accounts = await provider.connect({ onlyIfTrusted: false });
    if (accounts && accounts.length > 0) {
      triggerEvent("connect", accounts);
      return accounts;
    }
  } catch (error) {
  }
  throw new Error("Failed to connect to Phantom.");
}

// src/ethereum/disconnect.ts
async function disconnect() {
  const provider = await getProvider();
  if (!provider) {
    throw new Error("Provider not found.");
  }
  await provider.disconnect();
  triggerEvent("disconnect", []);
}

// src/ethereum/getAccounts.ts
async function getAccounts() {
  const provider = await getProvider();
  if (!provider) {
    throw new Error("Provider not found.");
  }
  return provider.getAccounts();
}

// src/ethereum/signMessage.ts
async function signPersonalMessage(message, address) {
  const provider = await getProvider();
  if (!provider) {
    throw new Error("Provider not found.");
  }
  if (!provider.isConnected) {
    await provider.connect({ onlyIfTrusted: false });
  }
  return provider.signPersonalMessage(message, address);
}
async function signTypedData(typedData, address) {
  const provider = await getProvider();
  if (!provider) {
    throw new Error("Provider not found.");
  }
  if (!provider.isConnected) {
    await provider.connect({ onlyIfTrusted: false });
  }
  return provider.signTypedData(typedData, address);
}

// src/ethereum/sendTransaction.ts
async function sendTransaction(transaction) {
  const provider = await getProvider();
  if (!provider) {
    throw new Error("Provider not found.");
  }
  if (!provider.isConnected) {
    await provider.connect({ onlyIfTrusted: false });
  }
  return provider.sendTransaction(transaction);
}
async function signTransaction(transaction) {
  const provider = await getProvider();
  if (!provider) {
    throw new Error("Provider not found.");
  }
  if (!provider.isConnected) {
    await provider.connect({ onlyIfTrusted: false });
  }
  return provider.signTransaction(transaction);
}

// src/ethereum/chainUtils.ts
async function getChainId() {
  const provider = await getProvider();
  if (!provider) {
    throw new Error("Provider not found.");
  }
  return provider.getChainId();
}
async function switchChain(chainId) {
  const provider = await getProvider();
  if (!provider) {
    throw new Error("Provider not found.");
  }
  return provider.switchChain(chainId);
}

// src/ethereum/plugin.ts
var Ethereum = class {
  constructor() {
    this._chainId = "0x1";
    this._accounts = [];
    this.bindProviderEvents();
  }
  get connected() {
    return this._accounts.length > 0;
  }
  get chainId() {
    return this._chainId;
  }
  get accounts() {
    return this._accounts;
  }
  async request(args) {
    const provider = await getProvider();
    if (!provider) {
      throw new Error("Provider not found.");
    }
    const providerInstance = provider.getProvider();
    if (!providerInstance) {
      throw new Error("Provider instance not found.");
    }
    return providerInstance.request(args);
  }
  async connect() {
    const accounts = await connect();
    this._accounts = accounts;
    return accounts;
  }
  async disconnect() {
    await disconnect();
    this._accounts = [];
  }
  signPersonalMessage(message, address) {
    return signPersonalMessage(message, address);
  }
  signTypedData(data, address) {
    return signTypedData(data, address);
  }
  signTransaction(transaction) {
    return signTransaction(transaction);
  }
  sendTransaction(transaction) {
    return sendTransaction(transaction);
  }
  async switchChain(chainId) {
    const hexChainId = typeof chainId === "string" ? chainId.toLowerCase().startsWith("0x") ? chainId.toLowerCase() : `0x${parseInt(chainId, 10).toString(16)}` : `0x${chainId.toString(16)}`;
    await switchChain(hexChainId);
    this._chainId = hexChainId;
  }
  async getChainId() {
    const chainId = await getChainId();
    const parsed = parseInt(chainId, 16);
    this._chainId = chainId;
    return parsed;
  }
  async getAccounts() {
    const accounts = await getAccounts();
    this._accounts = accounts;
    return accounts;
  }
  isConnected() {
    return this._accounts.length > 0;
  }
  on(event, listener) {
    addEventListener(event, listener);
  }
  off(event, listener) {
    removeEventListener(event, listener);
  }
  async bindProviderEvents() {
    try {
      const strategy = await getProvider();
      const provider = strategy.getProvider();
      if (provider) {
        provider.on("connect", async () => {
          try {
            const accounts = await provider.request({ method: "eth_accounts" });
            if (accounts?.length > 0) {
              this._accounts = accounts;
              triggerEvent("connect", accounts);
            }
          } catch {
          }
        });
        provider.on("disconnect", () => {
          this._accounts = [];
          const error = {
            code: 4900,
            message: "Provider disconnected"
          };
          triggerEvent("disconnect", error);
        });
        provider.on("accountsChanged", (accounts) => {
          this._accounts = accounts;
          triggerEvent("accountsChanged", accounts);
          if (accounts && accounts.length > 0) {
            triggerEvent("connect", accounts);
          }
        });
        provider.on("chainChanged", (chainId) => {
          this._chainId = chainId;
          triggerEvent("chainChanged", chainId);
        });
      }
    } catch (error) {
    }
  }
};
function createEthereumPlugin() {
  return {
    name: "ethereum",
    create: () => {
      return new Ethereum();
    }
  };
}

export {
  createSiweMessage,
  createEthereumPlugin
};
